import { ChangeDetectorRef, Component, Input, OnChanges, OnInit, ViewChild } from '@angular/core';
import { StripeService, StripeCardComponent } from 'ngx-stripe';
import {
  StripeCardElementChangeEvent,
  StripeCardElementOptions,
  StripeElementsOptions
} from '@stripe/stripe-js';
import {
  GetPaymentMethodsData
} from '@common/types/payment';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { UserProfile } from 'src/app/model/userProfile.model';
import { FirebaseApiService } from 'src/app/service/firebase-api.service';
import { MessageService } from 'primeng/api';
import { forkJoin } from 'rxjs';

@Component({
  selector: 'app-subscription',
  templateUrl: './subscription.component.html',
  styleUrls: ['./subscription.component.css',
    '../../../../node_modules/primeflex/primeflex.css']
})
export class SubscriptionComponent implements OnInit, OnChanges {
  @ViewChild(StripeCardComponent) card: StripeCardComponent;
  @Input() user: UserProfile;

  cardOptions: StripeCardElementOptions = {
    style: {
      base: {
        iconColor: '#666EE8',
        color: '#31325F',
        fontWeight: '300',
        fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
        fontSize: '18px',
        '::placeholder': {
          color: '#CFD7E0'
        }
      },
      invalid: {
        color: '#fa755a',
        iconColor: '#fa755a'
      }
    }
  };

  elementsOptions: StripeElementsOptions = {
    locale: 'en'
  };

  stripeForm: FormGroup;
  paymentElementsValid = false;
  useExistingPaymentMethod = false;
  errorMessage = '';
  successful = false;
  pending = true;
  processing = false;
  paymentMethods: GetPaymentMethodsData = [];

  isSubscribeForAdditional = false;
  dueAmount = 0;

  constructor(
    private fb: FormBuilder,
    private stripeService: StripeService,
    private cdr: ChangeDetectorRef,
    private firebaseApiService: FirebaseApiService,
    private messageService: MessageService
  ) {
  }

  ngOnInit(): void {
    this.buildStripeForm();
    this.getPaymentMethods();
    this.getUpcomingInvoices();
  }

  ngOnChanges() {
    this.buildStripeForm();
    this.getPaymentMethods();
    this.getUpcomingInvoices();
  }

  buildStripeForm() {
    this.stripeForm = this.fb.group({
      name: [this.user && (this.user.first + ' ' + this.user.last) || '', Validators.required]
    });
  }

  getPaymentMethods() {
    if (this.user && this.user.uId) {
      this.firebaseApiService.getPaymentMethods({
        stripeCustomerId: this.user.stripeId
      })
        .subscribe((paymentMethods) => {
          this.paymentMethods = paymentMethods;
          this.cdr.markForCheck();
        });
    }
  }

  getUpcomingInvoices() {
    if (this.user && this.user.uId) {
      const subscribeArr = [];
      if (this.user.standard_subscribe_id) {
        subscribeArr.push(
          this.firebaseApiService.retrieveUpcomingInvoice({
            stripeCustomerId: this.user.stripeId,
            subscriptionId: this.user.standard_subscribe_id
          })
        );
      }
      if (this.user.additional_subscribe_id) {
        subscribeArr.push(
          this.firebaseApiService.retrieveUpcomingInvoice({
            stripeCustomerId: this.user.stripeId,
            subscriptionId: this.user.additional_subscribe_id
          })
        );
      }

      if (subscribeArr.length > 0) {
        forkJoin(subscribeArr)
          .subscribe((invoices: any[]) => {
            this.dueAmount = 0;
            invoices.forEach(invoice => {
              this.dueAmount += invoice.amount_due;
            })
          })
      }
    }
  }

  createPaymentMethod(): void {
    this.processing = true;
    this.stripeService.createPaymentMethod({
      type: 'card',
      card: this.card.element,
      billing_details: {
        email: this.user.email,
        name: `${this.user.first} ${this.user.last}`
      },
    }).subscribe((res) => {
      if (res.paymentMethod) {
        this.firebaseApiService.attachPaymentMethodToCustomer({
          stripeCustomerId: this.user.stripeId,
          paymentMethodId: res.paymentMethod.id
        })
          .subscribe(res => {
            this.processing = false;
            this.card.element.clear();
            this.messageService.add({
              key: 'info', severity: 'success', summary: 'Success', detail: 'Your card is successfully added to your payment source .'
            });
            this.getPaymentMethods();
          }, err => {
            this.errorMessage = err.message;
            this.processing = false;
          });
      } else {
        console.log(res);
        this.processing = false;
        this.errorMessage = res.error.message;
      }
    }, (error) => {
      this.errorMessage = error.message;
      this.processing = false;
    }, () => {
      this.cdr.markForCheck();
    });
  }

  onChange(elementEvent: StripeCardElementChangeEvent) {
    this.errorMessage = '';
    const displayError = document.getElementById('card-error');
    this.paymentElementsValid = elementEvent.complete;
    if (elementEvent.error) {
      displayError.textContent = elementEvent.error.message;
    } else {
      displayError.textContent = '';
    }
  }

  createSubscription(membership) {
    this.processing = true;
    this.firebaseApiService.createSubscription({
      stripeCustomerId: this.user.stripeId,
      paymentMethodId: this.paymentMethods[0].id,
      email: this.user.email,
      membership
    })
      .subscribe(subscribe => {
        if (subscribe.status === 'active') {
          this.processing = false;
          this.messageService.add({
            key: 'info', severity: 'success', summary: 'Success', detail: 'Done'
          });
        } else {
          this.messageService.add({
            key: 'info', severity: 'error', summary: 'error', detail: 'Please confirm your payment.'
          });
          // this.stripeService.confirmCardPayment(
          //   subscribe.latest_invoice.payment_intent.client_secret,
          //   {
          //     payment_method: this.paymentMethods[0].id,
          //   }
          // )
          //   .subscribe((paymentIntent) => {
          //     this.processing = false;
          //     this.messageService.add({
          //       key: 'info', severity: 'success', summary: 'Success', detail: 'Done'
          //     });
          //   }, error => {
          //     this.processing = false;
          //     this.messageService.add({
          //       key: 'info', severity: 'error', summary: 'error', detail: error.message
          //     });
          //   })
        }
      }, err => {
        this.processing = false;
        this.messageService.add({
          key: 'info', severity: 'error', summary: 'Error', detail: err.message
        });
      });
  }

  cancelSubscription(membership) {
    this.processing = true;
    this.firebaseApiService.cancelSubscription({
      subscriptionId: membership === 'standard' ? this.user.standard_subscribe_id : this.user.additional_subscribe_id,
      membership: membership
    })
      .subscribe((subscription) => {
        this.processing = false;
        console.log(`Subscription status: ${subscription.status}`);
      }, err => {
        this.processing = false;
        this.messageService.add({
          key: 'info', severity: 'error', summary: 'Error', detail: err.message
        });
      });
  }
}
